import { useState, useEffect } from "react";
import axiosConfig from "../services/axiosConfig";
import setHeader from "../components/Functions/setHeader";
import SweetAlert from "sweetalert2";
import { CiImageOn } from "react-icons/ci";
import { Breadcrumb } from "react-bootstrap";

const HeaderLogo = () => {
    const [formData, setFormData] = useState({
        image: null,
    });

    const [currentLogo, setCurrentLogo] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);

    // Fetch current logo on component mount
    useEffect(() => {
        fetchLogoData();
    }, []);

    const fetchLogoData = async () => {
        setIsDataLoading(true);
        try {
            const response = await axiosConfig.get("get-logo", setHeader());
            if (!response.data.error) {
                const logoData = response.data.data;

                setCurrentLogo(logoData.image || logoData[0]?.image || "");
            } else {
                console.error("Failed to fetch logo data");
            }
        } catch (error) {
            console.error("Error fetching logo:", error);
        } finally {
            setIsDataLoading(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setFormData({
            image: file,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.image) {
            SweetAlert.fire({
                title: "Validation Error",
                text: "Please select a logo image",
                icon: "warning",
            });
            return;
        }

        setIsLoading(true);

        const submitFormData = new FormData();
        submitFormData.append("image", formData.image);

        // Log FormData contents
        for (let pair of submitFormData.entries()) {
            console.log(pair[0] + ": " + pair[1]);
        }

        axiosConfig
            .post("update-logo", submitFormData, setHeader())
            .then((res) => {
                setIsLoading(false);
                if (!res.data.error) {
                    SweetAlert.fire({
                        title: "Success",
                        text: res.data.messages || "Logo updated successfully",
                        icon: "success",
                    });

                    // Reset form and refresh logo data
                    setFormData({ image: null });
                    document.getElementById("image").value = "";
                    fetchLogoData();
                } else {
                    SweetAlert.fire({
                        title: "Oops!",
                        text: res.data.messages || "Failed to update logo. Try again!",
                        icon: "error",
                    });
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Backend error:", error);
                SweetAlert.fire({
                    title: "Error",
                    text: "An error occurred while updating the logo",
                    icon: "error",
                });
            });
    };

    const handleReset = () => {
        setFormData({ image: null });
        document.getElementById("image").value = "";
    };

    if (isDataLoading) {
        return (
            <div className="container py-4">
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <div className="card">
                            <div className="card-body text-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading logo data...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4">
            <Breadcrumb title="Header Logo" parent="Logo Management" />
            <div className="row">
                {/* Current Logo Display */}
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h5 className="card-title mb-0">
                                Current Logo
                            </h5>
                        </div>
                        <div className="card-body d-flex flex-column justify-content-center align-items-center">
                            {currentLogo ? (
                                <div className="text-center">
                                    <img
                                        src={currentLogo}
                                        alt="Current Logo"
                                        className="img-fluid rounded shadow"
                                        style={{
                                            maxWidth: "300px",
                                            maxHeight: "200px",
                                            objectFit: "contain",
                                        }}
                                    />
                                    <p className="text-muted mt-3 mb-0">Current Logo</p>
                                </div>
                            ) : (
                                <div className="text-center text-muted">
                                    <i className="fas fa-image fa-4x mb-3 opacity-50"></i>
                                    <p>No logo uploaded yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Logo Upload Form */}
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h5 className="card-title mb-0">
                                Update Logo
                            </h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label htmlFor="image" className="form-label fw-bold">
                                        Select New Logo Image
                                    </label>
                                    <input
                                        type="file"
                                        id="image"
                                        name="image"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                if (file.size > 200 * 1024) {
                                                    SweetAlert.fire({
                                                        title: "File Size Error",
                                                        text: "File size must not exceed 200 KB",
                                                        icon: "warning",
                                                    });
                                                    e.target.value = "";
                                                    return;
                                                }
                                                handleImageChange(e);
                                            }
                                        }}
                                        className="form-control"
                                        required
                                    />
                                    <div className="form-text">
                                        Recomented Size 600 X 600 | Max size: 200 KB
                                    </div>
                                </div>

                                {/* Preview new selected image */}
                                {formData.image && (
                                    <div className="mb-3">
                                        <div className="alert alert-info d-flex align-items-center">
                                            <div>
                                                <strong>Selected:</strong> {formData.image.name}
                                                <br />
                                                <small>Size: {(formData.image.size / 1024).toFixed(2)} KB</small>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="d-grid gap-2">
                                    <button
                                        type="submit"
                                        className="btn btn-success"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Updating Logo...
                                            </>
                                        ) : (
                                            <>
                                                Update Logo
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleReset}
                                        className="btn btn-secondary"
                                        disabled={isLoading}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Instructions Card */}
            {/* <div className="row">
        <div className="col-12">
          <div className="card border-info">
            <div className="card-header bg-light">
              <h6 className="card-title mb-0 text-info">
                <i className="fas fa-lightbulb me-2"></i>Instructions
              </h6>
            </div>
            <div className="card-body">
              <ul className="list-unstyled mb-0">
                <li className="mb-2">
                  <i className="fas fa-arrow-right text-primary me-2"></i>
                  Select a new logo image to replace the current one
                </li>
                <li className="mb-2">
                  <i className="fas fa-arrow-right text-primary me-2"></i>
                  Supported image formats: JPG, PNG, GIF
                </li>
                <li className="mb-2">
                  <i className="fas fa-arrow-right text-primary me-2"></i>
                  Maximum file size: 1 MB
                </li>
                <li>
                  <i className="fas fa-arrow-right text-primary me-2"></i>
                  The logo will be updated immediately after successful upload
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div> */}
        </div>
    );
};

export default HeaderLogo;